import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Tooltip } from "antd";
import { Sparkle } from "@phosphor-icons/react";
import styled from "styled-components";
import { selectIsAnalysisRunningForDocument } from "../../slice/AISlice";
import { buildAIPaneSearch } from "../../utils/aiPaneUrl";
import { brand, text } from "../../theme/tokens";

/**
 * Phase 2 Analyze affordance.
 *
 * The single primary trigger for opening the AI panel on the Analysis
 * tab from any document context. Three render variants share the same
 * running-state logic so disabled/tooltip behaviour is consistent
 * everywhere a user can fire deep analysis:
 *
 *   - Document viewer header button (`variant="header"`, default).
 *   - Viewer More-menu item (`variant="menuItem"` — returns a plain text
 *     row that callers wrap in their own `<MoreButton>` menu option).
 *   - Briefing-tab footer link (handled directly by `BriefingTab.js`'s
 *     `RunDeepAnalysisLink`; not a render variant here, just sharing the
 *     same selector hook).
 *
 * The component does NOT itself dispatch `triggerAnalysisJob`. Phase 1
 * established the contract that the Analysis tab owns the trigger UX
 * (its `claimed_only` checkbox lives there, and the button-spinner /
 * empty / running-stub states are all driven from `AnalysisTab.js`).
 * The header / row-menu Analyze affordances are wayfinding — they open
 * the panel on the Analysis tab so the user always confirms the run
 * from one canonical surface. This avoids surprise auto-triggers from
 * row menus (which never offered the `claimed_only` toggle to the user)
 * and keeps Phase 3's polling state machine driven by a single mount
 * point.
 *
 * Disabled state is sourced from `selectIsAnalysisRunningForDocument`
 * so all three Analyze surfaces (header, More menu, briefing CTA) read
 * from the same Redux truth. When a job is running the button label
 * flips to "Analyzing…" with a tooltip that explains the panel is
 * already open.
 */

const HeaderButton = styled(Button)`
  &.ant-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 30px;
    padding: 0 12px;
    border-radius: 6px;
    font-size: 13px;
    white-space: nowrap;
    border-color: ${brand.primary};
    color: ${brand.primary};
    font-weight: 600;

    &:hover:not(:disabled) {
      color: ${brand.primaryHover};
      border-color: ${brand.primaryHover};
      background: rgba(12, 83, 187, 0.04);
    }
    &:focus-visible {
      outline: 2px solid ${brand.primary};
      outline-offset: 1px;
    }
    &:disabled {
      color: ${text.disabled};
      border-color: ${text.disabled};
      background: transparent;
      opacity: 0.7;
    }
  }
`;

const AnalyzeButton = ({ documentId, label }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isRunning = useSelector((state) =>
    selectIsAnalysisRunningForDocument(state, documentId)
  );

  const handleClick = useCallback(() => {
    if (!documentId) return;
    const search = buildAIPaneSearch(location.search, "analysis");
    navigate(`${location.pathname}${search}`, { replace: false });
  }, [documentId, location.pathname, location.search, navigate]);

  const visibleLabel = isRunning ? "Analyzing…" : label;
  const tooltipTitle = isRunning
    ? "An analysis is already running on this document. The Brief tab shows progress."
    : "Run deep AI analysis on this document";

  const button = (
    <HeaderButton
      type="default"
      size="middle"
      icon={<Sparkle size={16} weight="fill" />}
      onClick={handleClick}
      disabled={!documentId || isRunning}
      aria-label={tooltipTitle}
      data-testid="ai-analyze-button"
    >
      {visibleLabel}
    </HeaderButton>
  );

  return (
    <Tooltip title={tooltipTitle} mouseEnterDelay={0.3}>
      {/*
       * antd Tooltips suppress on disabled buttons unless the disabled
       * button is wrapped — wrapping with a span keeps the tooltip
       * working in the running state without changing the layout.
       */}
      <span style={{ display: "inline-flex" }}>{button}</span>
    </Tooltip>
  );
};

AnalyzeButton.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
};

AnalyzeButton.defaultProps = {
  documentId: null,
  label: "Analyze",
};

export default AnalyzeButton;
